import { useState } from 'react';
import {
  TextArea,
  Tag,
  InlineLoading,
  InlineNotification,
  Button,
} from '@carbon/react';
import { Search, Reset } from '@carbon/icons-react';
import { useMutation } from '@tanstack/react-query';
import { knowledgeSearchApi } from '@/lib/api/knowledge-search.api';
import type { SemanticHit } from '@snxt/snxt-onto-share';
import './FacetSearch.scss';

interface SemanticFacetProps {
  kbId: string;
}

export function SemanticFacet({ kbId }: SemanticFacetProps) {
  const [query, setQuery] = useState('');

  const { data, isPending, isError, mutate, reset } = useMutation({
    mutationFn: (q: string) =>
      knowledgeSearchApi.searchSemantic({ kbId, query: q, limit: 10 }),
  });

  const handleSearch = () => {
    if (query.trim()) mutate(query.trim());
  };

  const handleReset = () => {
    setQuery('');
    reset();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSearch();
  };

  const hits: SemanticHit[] = data?.hits ?? [];

  return (
    <div className="facet-search__panel">
      {/* Query area */}
      <div className="facet-search__semantic-bar">
        <TextArea
          id="semantic-query"
          labelText="Ask a question"
          placeholder="What is the melting point of compound X? (Cmd+Enter to search)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          className="facet-search__semantic-textarea"
        />

        <div className="facet-search__semantic-actions">
          <Button
            kind="primary"
            size="md"
            renderIcon={Search}
            onClick={handleSearch}
            disabled={isPending || !query.trim()}
          >
            Search
          </Button>

          <Button
            kind="ghost"
            size="md"
            renderIcon={Reset}
            onClick={handleReset}
            hasIconOnly
            iconDescription="Reset"
          />
        </div>
      </div>

      <p className="facet-search__semantic-hint">
        Semantic search embeds your question and retrieves the most similar Knowledge Units by cosine similarity. ⌘+Enter to submit.
      </p>

      {/* Status */}
      {isPending && <InlineLoading description="Generating embedding and searching…" />}

      {isError && (
        <InlineNotification
          kind="error"
          title="Semantic search failed"
          subtitle="Make sure a vector embedding model is configured."
          hideCloseButton
        />
      )}

      {data && hits.length === 0 && (
        <div className="facet-search__empty">No similar KUs found. Try rephrasing your question.</div>
      )}

      {/* Results */}
      {hits.length > 0 && (
        <>
          <div className="facet-search__count">
            Top {hits.length} KU match{hits.length !== 1 ? 'es' : ''} for "{data?.query}"
          </div>

          <div className="facet-search__semantic-results">
            {hits.map((h, i) => {
              const score = Math.round(h.similarity * 100);
              const scoreType = score >= 80 ? 'green' : score >= 60 ? 'teal' : score >= 40 ? 'blue' : 'gray';
              return (
                <div key={`${h.knowledge_unit_id}-${i}`} className="facet-search__semantic-card">
                  <div className="facet-search__semantic-card-header">
                    <Tag size="sm" type={scoreType}>
                      {score}% match
                    </Tag>
                    <span className="facet-search__semantic-source">
                      {h.knowledge_object_name ?? h.knowledge_object_id}
                    </span>
                  </div>
                  {h.snippet && (
                    <p className="facet-search__semantic-snippet">{h.snippet}</p>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Made with Bob
